import {
  orderItemKitComponents,
  orderItems,
  orders,
} from "@/server/platform/db/schema/orders";
import { productVariants } from "@/server/platform/db/schema/products";
import type { DbClient } from "@/server/platform/db/client";
import type { DbTransaction } from "@/shared/types";
import {
  parseUnitsPerSale,
  saleQtyToInventoryQty,
} from "@/modules/products/domain/shared-stock";
import { and, eq, ne } from "drizzle-orm";

/** รวมจำนวนสต็อก (หน่วยคลัง) ที่ draft orders จองไว้ แยกตาม pool variant */
export async function listDraftReservedInventoryByPool(
  client: DbTransaction | DbClient,
  options?: { excludeOrderId?: string },
): Promise<Map<string, number>> {
  const reserved = new Map<string, number>();

  const draftFilter = options?.excludeOrderId
    ? and(eq(orders.status, "draft"), ne(orders.id, options.excludeOrderId))
    : eq(orders.status, "draft");

  const simpleRows = await client
    .select({
      variantId: orderItems.variantId,
      quantity: orderItems.quantity,
      inventoryVariantId: productVariants.inventoryVariantId,
      unitsPerSale: productVariants.unitsPerSale,
    })
    .from(orderItems)
    .innerJoin(orders, eq(orders.id, orderItems.orderId))
    .innerJoin(productVariants, eq(productVariants.id, orderItems.variantId))
    .where(and(draftFilter, ne(orderItems.lineType, "ad_hoc_kit")));

  for (const row of simpleRows) {
    addPoolReservation(reserved, {
      poolVariantId: row.inventoryVariantId ?? row.variantId,
      saleQuantity: row.quantity,
      unitsPerSale: row.unitsPerSale,
      usesInventoryUnits: Boolean(row.inventoryVariantId),
    });
  }

  const kitRows = await client
    .select({
      kitLineQty: orderItems.quantity,
      componentQtyPerKit: orderItemKitComponents.quantity,
      inventoryVariantId: productVariants.inventoryVariantId,
      unitsPerSale: productVariants.unitsPerSale,
      componentVariantId: orderItemKitComponents.componentVariantId,
    })
    .from(orderItemKitComponents)
    .innerJoin(orderItems, eq(orderItems.id, orderItemKitComponents.orderItemId))
    .innerJoin(orders, eq(orders.id, orderItems.orderId))
    .innerJoin(
      productVariants,
      eq(productVariants.id, orderItemKitComponents.componentVariantId),
    )
    .where(and(draftFilter, eq(orderItems.lineType, "ad_hoc_kit")));

  for (const row of kitRows) {
    addPoolReservation(reserved, {
      poolVariantId: row.inventoryVariantId ?? row.componentVariantId,
      saleQuantity: row.componentQtyPerKit * row.kitLineQty,
      unitsPerSale: row.unitsPerSale,
      usesInventoryUnits: Boolean(row.inventoryVariantId),
    });
  }

  return reserved;
}

function addPoolReservation(
  reserved: Map<string, number>,
  params: {
    poolVariantId: string;
    saleQuantity: number;
    unitsPerSale: string;
    usesInventoryUnits: boolean;
  },
) {
  if (params.saleQuantity <= 0) return;

  const units = parseUnitsPerSale(params.unitsPerSale);
  const inventoryQty = params.usesInventoryUnits
    ? saleQtyToInventoryQty(params.saleQuantity, units)
    : params.saleQuantity;

  reserved.set(
    params.poolVariantId,
    (reserved.get(params.poolVariantId) ?? 0) + inventoryQty,
  );
}
